import clsx from "clsx";
import { isNil } from "lodash-es";
import type { CSSProperties, FC, HTMLAttributes } from "react";
import "./Score.scss";

import { rollbarAndLogError } from "../../utils/logger";

interface ScoreIconProps extends HTMLAttributes<HTMLDivElement> {
  className?: string;
  style?: CSSProperties;
  value?: number;
  size?: "xs" | "s" | "m" | "l";
  unselected?: boolean;
  floor?: boolean;
  disabled?: boolean;
}

export const ScoreIcon: FC<ScoreIconProps> = ({
  className = "",
  style,
  value,
  size = "s",
  unselected,
  floor = false,
  disabled,
  ...htmlAttributes
}) => {
  // If value is a float, the coloring will be done based on the closest value
  // or the floor value.
  const integerValue = value ? (floor ? Math.floor(value) : Math.round(value)) : 0;
  const isInt = integerValue === value;

  const isValid = !isNil(value) && !isNaN(value);
  const display = isValid ? (isInt ? value : value.toFixed(1)) : "-";

  if (value && !(integerValue >= 0 && integerValue <= 5)) {
    rollbarAndLogError(`[ScoreIcon] Unrecognized score value ${value}`);
  }

  const scoreBgClass = clsx(
    `score score-${size}`,
    isValid && `value-${integerValue}`,
    unselected && "unselected",
    !unselected && disabled && "disabled",
    // Floats don't fit into the small size, so we make them a bit wider
    !isInt && isValid && "score-wide",
    className,
  );

  return (
    <div className={scoreBgClass} style={style}>
      <div
        aria-label={`score ${!isNil(value) ? value : "blank"}`}
        {...htmlAttributes}
        className="score-text"
      >
        {display}
      </div>
    </div>
  );
};
